import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, throwError, from } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

/** Backend error body: single { errorKey, message } or multiple { errors: [{ errorKey, message }] } */
interface ApiErrorBody {
  errorKey?: string;
  message?: string;
  errors?: Array<{ errorKey?: string; message?: string }>;
}

@Injectable()
export class ApiErrorToastInterceptor implements HttpInterceptor {
  constructor(
    private readonly messageService: MessageService,
    private readonly translocoService: TranslocoService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        // Auth interceptori o'z oqimini boshqarsin
        if (err.status === 401) {
          return throwError(() => err);
        }

        // Faqat 400 statusda toast ko'rsatamiz
        if (err.status !== 400) {
          return throwError(() => err);
        }

        const raw = err.error;

        // Blob bo'lsa (NSwag, file, va hokazo) – matnini o'qib, modelga mos bo'lsa toast ko'rsatamiz
        if (raw instanceof Blob) {
          return from(raw.text()).pipe(
            map(text => this.tryParseBody(text)),
            switchMap(body => {
              if (!body) {
                return throwError(() => err);
              }
              this.showToastFromBody(body);
              return throwError(() => err);
            })
          );
        }

        const body = this.tryParseBody(raw);
        if (!body) {
          return throwError(() => err);
        }

        this.showToastFromBody(body);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Har qanday raw qiymatdan (object/string) ApiErrorBody ni ajratib olishga urinish.
   * Mos kelmasa null qaytadi.
   */
  private tryParseBody(raw: unknown): ApiErrorBody | null {
    if (raw == null) return null;

    // Object: bevosita strukturani tekshiramiz
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return this.getApiErrorBodyFromObject(raw as Record<string, unknown>);
    }

    // String: JSON bo'lsa parse qilib ko'ramiz
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw) as unknown;
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return this.getApiErrorBodyFromObject(parsed as Record<string, unknown>);
        }
      } catch {
        return null;
      }
    }

    return null;
  }

  /**
   * Object strukturasini ApiErrorBody modeliga mosligini tekshiradi.
   * Kamida bitta: errorKey (string) yoki message (string) yoki errors[] bo'lishi shart.
   */
  private getApiErrorBodyFromObject(obj: Record<string, unknown>): ApiErrorBody | null {
    const hasErrorKey = typeof obj['errorKey'] === 'string';
    const hasMessage = typeof obj['message'] === 'string';

    const errors = Array.isArray(obj['errors']) ? (obj['errors'] as unknown[]) : null;
    const hasErrorsArray =
      !!errors &&
      errors.length > 0 &&
      errors.some(
        (e) =>
          e &&
          typeof e === 'object' &&
          (typeof (e as any).errorKey === 'string' ||
            typeof (e as any).message === 'string')
      );

    if (!hasErrorKey && !hasMessage && !hasErrorsArray) {
      return null;
    }

    return {
      errorKey: hasErrorKey ? (obj['errorKey'] as string) : undefined,
      message: hasMessage ? (obj['message'] as string) : undefined,
      errors: hasErrorsArray ? (errors as Array<{ errorKey?: string; message?: string }>) : undefined,
    };
  }

  private showToastFromBody(body: ApiErrorBody): void {
    const { summary, detail } = this.parseErrorFromBody(body);
    this.messageService.add({
      severity: 'error',
      summary: detail || undefined, // birinchi qatorda matn
      detail: summary,              // ikkinchi qatorda sarlavha/kalit
      life: 5000,
    });
  }

  private parseErrorFromBody(body: ApiErrorBody): { summary: string; detail: string | null } {
    if (body.errors?.length) {
      const first = body.errors[0];
      const errorKey = first.errorKey || 'UNKNOWN';
      return this.summaryDetail(errorKey, first.message ?? null);
    }
    const errorKey = body.errorKey || 'UNKNOWN';
    return this.summaryDetail(errorKey, body.message ?? null);
  }

  private summaryDetail(
    errorKey: string,
    message: string | null,
  ): { summary: string; detail: string | null } {
    // Sarlavha: errors.<errorKey>
    const summaryKey = 'errors.' + errorKey;
    const summaryT = this.translocoService.translate(summaryKey);
    const summary = summaryT !== summaryKey ? summaryT : errorKey;

    // Matn: agar message kalit bo'lsa errors.<message> orqali tarjima qilishga urinamiz
    let detail: string | null = null;
    if (message != null && message.trim()) {
      const detailKey = 'errors.' + message.trim();
      const detailT = this.translocoService.translate(detailKey);
      detail = detailT !== detailKey ? detailT : message;
    }

    return { summary, detail };
  }
}

